import React, { useEffect, useState, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite query param for asset URL
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { FileText, Loader2 } from 'lucide-react';

// Configure PDF.js worker using locally bundled worker from Vite
if (typeof window !== 'undefined' && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
    try {
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
    } catch {
        // fallback to cdn only if local worker url resolution fails
        try {
            pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version || '4.10.38'}/build/pdf.worker.min.mjs`;
        } catch {}
    }
}

interface PdfThumbnailProps {
    url: string;
    alt?: string;
    className?: string;
    scale?: number;
}

export const PdfThumbnail: React.FC<PdfThumbnailProps> = ({ 
    url, 
    alt = 'PDF Document Thumbnail', 
    className = '',
    scale = 2.0
}) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        if (!url) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const loadPdfDoc = async (targetUrl: string) => {
            const loadingTask = pdfjsLib.getDocument({
                url: targetUrl,
                withCredentials: false,
            });
            return await loadingTask.promise;
        };

        const renderPage = async () => {
            try {
                setLoading(true);
                setError(false);

                let pdf: any;
                try {
                    pdf = await loadPdfDoc(url);
                } catch (directErr) {
                    // If direct fetch fails (e.g. CORS on S3), attempt loading via backend proxy
                    if (url.startsWith('http') && !url.includes('/public/file-proxy')) {
                        const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1').replace(/\/+$/, '');
                        const proxyUrl = `${apiBase}/admin/students/public/file-proxy?url=${encodeURIComponent(url)}`;
                        pdf = await loadPdfDoc(proxyUrl);
                    } else {
                        throw directErr;
                    }
                }

                if (cancelled) return;

                const page = await pdf.getPage(1);
                if (cancelled) return;

                // Determine scale for crisp thumbnail rendering
                const viewport = page.getViewport({ scale: scale || 2.0 });
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                if (!context) {
                    throw new Error('Canvas 2D context not available');
                }

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                };

                await (page.render(renderContext as any) as any).promise;
                if (cancelled) return;

                const dataUrl = canvas.toDataURL('image/png');
                if (isMounted.current) {
                    setImageUrl(dataUrl);
                    setLoading(false);
                }
            } catch (err) {
                console.warn('Could not generate PDF thumbnail with PDF.js:', err);
                if (isMounted.current) {
                    setError(true);
                    setLoading(false);
                }
            }
        };

        renderPage();

        return () => {
            cancelled = true;
            isMounted.current = false;
        };
    }, [url]);

    if (loading) {
        return (
            <div className={`bg-gray-100 rounded-lg flex items-center justify-center animate-pulse ${className}`}>
                <Loader2 size={20} className="animate-spin text-gray-400" />
            </div>
        );
    }

    if (error || !imageUrl) {
        return (
            <div className={`bg-gradient-to-br from-gray-50 to-gray-200 rounded-lg flex flex-col items-center justify-center p-3 text-gray-400 border border-gray-200 ${className}`}>
                <FileText size={28} className="text-gray-400 mb-1" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">PDF Preview</span>
            </div>
        );
    }

    return (
        <img 
            src={imageUrl} 
            alt={alt} 
            className={`object-contain bg-white rounded-lg shadow-xs border border-gray-200/80 transition-transform group-hover:scale-[1.02] duration-200 ${className}`} 
        />
    );
};

export default PdfThumbnail;
